import { UI_STRINGS } from '@/lib/constants/ui-strings';

/**
 * Option lists and label helpers shared by the investor profile cards and
 * header. Split out of the former 438-line `PreferencesSection` (MYN-217;
 * mnr-standards file-size rule).
 */
const S = UI_STRINGS.INVESTOR_PROFILE;

/**
 * Values must match the backend's generated Prisma `RevenueRange` enum
 * members, not the `@map()`'d DB column names - `@map` only renames the
 * underlying Postgres enum value, `class-validator`'s `@IsEnum` checks
 * against the Prisma client's own member names (e.g. `range_500k_1m`, not
 * `500k_1m`). Sending the bare `@map` name fails validation on every save.
 */
export const REVENUE_OPTIONS = [
  { value: 'zero', label: '$0' },
  { value: 'under_250k', label: 'Under $250K' },
  { value: 'range_250k_500k', label: '$250K - $500K' },
  { value: 'range_500k_1m', label: '$500K - $1M' },
  { value: 'range_1m_2m', label: '$1M - $2M' },
  { value: 'range_2m_5m', label: '$2M - $5M' },
  { value: 'range_5m_10m', label: '$5M - $10M' },
  { value: 'range_10m_plus', label: '$10M+' },
];

export const RECURRING_REVENUE_OPTIONS = [
  { value: 'only_recurring', label: 'Only Recurring' },
  { value: 'prefers_recurring', label: 'Prefers Recurring' },
  { value: 'no_preference', label: 'No Preference' },
];

export const LEAD_OR_FOLLOW_OPTIONS = [
  { value: 'leads_rounds', label: 'Leads Rounds' },
  { value: 'follows_only', label: 'Follows Only' },
  { value: 'either', label: 'Either' },
];

export const INVESTOR_PREF_LEVEL_OPTIONS = [
  { value: 'only_invests_in', label: S.ONLY_INVESTS_IN },
  { value: 'open_to', label: S.OPEN_TO },
  { value: 'no_preference', label: S.NO_PREFERENCE },
] as const;

export const MARKET_MODEL_OPTIONS = [
  { value: 'only', label: S.ONLY },
  { value: 'sometimes', label: S.SOMETIMES },
  { value: 'never', label: S.NEVER },
] as const;

export const MARKET_MODEL_FIELDS = [
  { name: 'prefB2b' as const, label: 'B2B' },
  { name: 'prefB2c' as const, label: 'B2C' },
  { name: 'prefB2g' as const, label: 'B2G' },
  { name: 'prefSaas' as const, label: 'SaaS' },
  { name: 'prefAi' as const, label: 'AI' },
];

// Both 3-option SegmentedControls (Female-Led/Underrepresented level, and each
// Market Model row) color their 1st/2nd/3rd option navy/green/red - one
// shared sequence instead of two option-shaped copies of the same three tones.
const THREE_OPTION_TONE_SEQUENCE = ['navy', 'success', 'danger'] as const;

function buildToneByValue<T extends { value: string }>(
  options: readonly T[],
): Record<T['value'], (typeof THREE_OPTION_TONE_SEQUENCE)[number]> {
  return Object.fromEntries(
    options.map((option, index) => [option.value, THREE_OPTION_TONE_SEQUENCE[index]]),
  ) as Record<T['value'], (typeof THREE_OPTION_TONE_SEQUENCE)[number]>;
}

export const INVESTOR_PREF_LEVEL_TONE_BY_VALUE = buildToneByValue(INVESTOR_PREF_LEVEL_OPTIONS);
export const MARKET_MODEL_TONE_BY_VALUE = buildToneByValue(MARKET_MODEL_OPTIONS);

export function labelFor(
  options: readonly { value: string; label: string }[],
  value?: string | null,
): string | undefined {
  return options.find((o) => o.value === value)?.label ?? undefined;
}

/**
 * Values must match the backend's Prisma `InvestorType` enum members exactly.
 * Distinct from the Startup module's own `INVESTOR_TYPE_OPTIONS` (Startup's
 * "Investor Types Sought" ends in "Open to All", not "Other") - single-feature
 * use, kept local per mnr-standards.
 */
export const INVESTOR_TYPE_OPTIONS = [
  { value: 'angel', label: 'Angel' },
  { value: 'vc_fund', label: 'VC Fund' },
  { value: 'family_office', label: 'Family Office' },
  { value: 'corporate_strategic', label: 'Corporate or Strategic' },
  { value: 'other', label: 'Other' },
];

export function investorTypeLabel(value?: string | null): string | undefined {
  return INVESTOR_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value ?? undefined;
}
